/**
 * Performance monitoring utility for Firebase queries
 */

class PerformanceMonitor {
  constructor() {
    this.metrics = {
      queries: {},
      errors: {},
      slowQueries: [],
    };
    this.enabled =
      process.env.NODE_ENV === "development" ||
      process.env.REACT_APP_ENABLE_PERFORMANCE_MONITORING === "true";
  }

  /**
   * Track query performance
   * @param {string} queryName - Name of the query
   * @param {Function} queryFn - The query function to execute
   * @param {Object} options - Options for tracking
   * @returns {Promise} - Query result
   */
  async trackQuery(queryName, queryFn, options = {}) {
    if (!this.enabled) {
      return await queryFn();
    }

    const startTime = performance.now();
    const startMemory = performance.memory?.usedJSHeapSize || 0;

    try {
      const result = await queryFn();
      const endTime = performance.now();
      const endMemory = performance.memory?.usedJSHeapSize || 0;

      const duration = endTime - startTime;
      const memoryUsed = endMemory - startMemory;

      this.recordQuery(queryName, {
        duration,
        memoryUsed,
        success: true,
        timestamp: new Date().toISOString(),
        ...options,
      });

      // Log slow queries
      if (duration > (options.slowThreshold || 1000)) {
        this.recordSlowQuery(queryName, duration, options);
      }

      return result;
    } catch (error) {
      const endTime = performance.now();
      const duration = endTime - startTime;

      this.recordQuery(queryName, {
        duration,
        success: false,
        error: error.message,
        timestamp: new Date().toISOString(),
        ...options,
      });

      this.recordError(queryName, error);
      throw error;
    }
  }

  /**
   * Record query metrics
   * @param {string} queryName - Name of the query
   * @param {Object} metrics - Query metrics
   */
  recordQuery(queryName, metrics) {
    if (!this.metrics.queries[queryName]) {
      this.metrics.queries[queryName] = {
        count: 0,
        totalDuration: 0,
        averageDuration: 0,
        successCount: 0,
        errorCount: 0,
        minDuration: Infinity,
        maxDuration: 0,
      };
    }

    const query = this.metrics.queries[queryName];
    query.count++;
    query.totalDuration += metrics.duration;
    query.averageDuration = query.totalDuration / query.count;

    if (metrics.success) {
      query.successCount++;
    } else {
      query.errorCount++;
    }

    query.minDuration = Math.min(query.minDuration, metrics.duration);
    query.maxDuration = Math.max(query.maxDuration, metrics.duration);

    // Keep only last 100 queries per type
    if (query.count > 100) {
      query.count = 100;
      query.totalDuration = query.averageDuration * 100;
    }
  }

  /**
   * Record slow query
   * @param {string} queryName - Name of the query
   * @param {number} duration - Query duration
   * @param {Object} options - Query options
   */
  recordSlowQuery(queryName, duration, options) {
    this.metrics.slowQueries.push({
      queryName,
      duration,
      timestamp: new Date().toISOString(),
      ...options,
    });

    // Keep only last 50 slow queries
    if (this.metrics.slowQueries.length > 50) {
      this.metrics.slowQueries = this.metrics.slowQueries.slice(-50);
    }

    console.warn(`Slow query detected: ${queryName} took ${duration.toFixed(2)}ms`);
  }

  /**
   * Record error
   * @param {string} queryName - Name of the query
   * @param {Error} error - Error object
   */
  recordError(queryName, error) {
    if (!this.metrics.errors[queryName]) {
      this.metrics.errors[queryName] = [];
    }

    this.metrics.errors[queryName].push({
      message: error.message,
      stack: error.stack,
      timestamp: new Date().toISOString(),
    });

    // Keep only last 20 errors per query
    if (this.metrics.errors[queryName].length > 20) {
      this.metrics.errors[queryName] = this.metrics.errors[queryName].slice(-20);
    }
  }

  /**
   * Get performance report
   * @returns {Object} Performance metrics
   */
  getReport() {
    return {
      queries: this.metrics.queries,
      slowQueries: this.metrics.slowQueries.slice(-10), // Last 10 slow queries
      errors: Object.keys(this.metrics.errors).reduce((acc, key) => {
        acc[key] = this.metrics.errors[key].slice(-5); // Last 5 errors per query
        return acc;
      }, {}),
      summary: this.getSummary(),
    };
  }

  /**
   * Get performance summary
   * @returns {Object} Summary statistics
   */
  getSummary() {
    const queries = Object.values(this.metrics.queries);
    const totalQueries = queries.reduce((sum, q) => sum + q.count, 0);
    const totalDuration = queries.reduce((sum, q) => sum + q.totalDuration, 0);
    const totalErrors = queries.reduce((sum, q) => sum + q.errorCount, 0);

    return {
      totalQueries,
      averageQueryDuration: totalQueries > 0 ? totalDuration / totalQueries : 0,
      errorRate: totalQueries > 0 ? (totalErrors / totalQueries) * 100 : 0,
      slowQueriesCount: this.metrics.slowQueries.length,
      uniqueQueries: Object.keys(this.metrics.queries).length,
    };
  }

  /**
   * Clear metrics
   */
  clear() {
    this.metrics = {
      queries: {},
      errors: {},
      slowQueries: [],
    };
  }

  /**
   * Log performance report to console
   */
  logReport() {
    if (!this.enabled) return;

    const report = this.getReport();
    console.group("🔥 Firebase Query Performance Report");
    console.table(report.summary);

    if (report.slowQueries.length > 0) {
      console.group("🐌 Slow Queries");
      report.slowQueries.forEach((query) => {
        console.log(`${query.queryName}: ${query.duration.toFixed(2)}ms`);
      });
      console.groupEnd();
    }

    if (Object.keys(report.errors).length > 0) {
      console.group("❌ Errors");
      Object.entries(report.errors).forEach(([queryName, errors]) => {
        console.log(`${queryName}: ${errors.length} errors`);
      });
      console.groupEnd();
    }

    console.groupEnd();
  }
}

// Create singleton instance
const performanceMonitor = new PerformanceMonitor();

// Export for use in components
export default performanceMonitor;

// Export wrapper function for easy use
export const trackQuery = (queryName, queryFn, options = {}) => {
  return performanceMonitor.trackQuery(queryName, queryFn, options);
};

// Export for debugging
export const getPerformanceReport = () => performanceMonitor.getReport();
export const logPerformanceReport = () => performanceMonitor.logReport();
